import * as React from "react"
import * as PopoverPrimitive from "@radix-ui/react-popover"

import { cn } from "../../utils"

const Popover = PopoverPrimitive.Root

const PopoverTrigger = PopoverPrimitive.Trigger

const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>
>(({ className, align = "center", sideOffset = 4, ...props }, ref) => {
  // Radix's popper wrapper has will-change:transform (creates stacking context)
  // but no z-index, so it sits at z-index:auto in root stacking context.
  // This causes popovers to be unclickable inside Dialogs whose overlay has
  // a higher z-index. We fix this by setting z-index on the wrapper directly.
  const setRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      // Forward the consumer ref
      if (typeof ref === "function") ref(node)
      else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node

      // Fix the Radix popper wrapper for use inside Dialogs:
      // 1. z-index: Dialog overlay is z-9999, wrapper needs to be above it
      // 2. pointer-events: Modal Dialogs set pointer-events:none on <body>,
      //    so the wrapper (a body child) inherits that and blocks all clicks
      if (node) {
        const wrapper = node.closest("[data-radix-popper-content-wrapper]")
        if (wrapper instanceof HTMLElement) {
          // Use setProperty with 'important' so inline !important beats
          // any stylesheet !important rule (e.g. shell-app's global CSS
          // that sets z-index: 9999 !important on popper wrappers).
          wrapper.style.setProperty("z-index", "999999", "important")
          wrapper.style.pointerEvents = "auto"
        }
      }
    },
    [ref]
  )

  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        ref={setRef}
        align={align}
        sideOffset={sideOffset}
        className={cn(
          "z-[999999] w-80 rounded-md border bg-white p-4 text-slate-950 shadow-lg outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-popover-content-transform-origin]",
          className
        )}
        {...props}
      />
    </PopoverPrimitive.Portal>
  )
})
PopoverContent.displayName = PopoverPrimitive.Content.displayName

export { Popover, PopoverTrigger, PopoverContent }
